import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp, Download, Calculator, Users, ChevronUp, ChevronDown,
  Filter, X, AlertTriangle, RefreshCw, Eye, Award
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';

const API = process.env.REACT_APP_API_URL || '';
const VERT      = '#1B5E20';
const VERT_MED  = '#388E3C';
const VERT_L    = '#E8F5E9';
const ROUGE     = '#C62828';
const GRIS      = '#F7F7F7';
const GRIS_BORD = '#E0E0E0';

const formatFCFA = (n) =>
  new Intl.NumberFormat('fr-FR').format(Math.round(parseFloat(n) || 0)) + ' FCFA';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('fr-FR') : '–';

// ─── Couleur ligne selon ancienneté ────────────────────────────────────────
const couleurLigne = (annees) => {
  if (annees >= 20) return '#fce4ec';
  if (annees >= 10) return '#fff8e1';
  if (annees >= 5)  return '#f0f7f0';
  return '#ffffff';
};

// ─── Composant : Card stat ─────────────────────────────────────────────────
const StatCard = ({ label, valeur, sous, Ico, color }) => (
  <div style={{
    background: '#fff', borderRadius: 10, padding: '18px 20px',
    border: `1px solid ${GRIS_BORD}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    display: 'flex', alignItems: 'center', gap: 16,
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 10,
      background: color + '1A', display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <Ico size={22} color={color} />
    </div>
    <div>
      <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a1a', marginTop: 2 }}>{valeur}</div>
      {sous && <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>{sous}</div>}
    </div>
  </div>
);

// ─── Composant : Barre de progression tranche ──────────────────────────────
const BarreTranche = ({ label, montant, total, couleur }) => {
  const pct = total > 0 ? Math.min((montant / total) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: '#555', fontWeight: 600 }}>{label}</span>
        <span style={{ fontWeight: 700, color: couleur }}>{formatFCFA(montant)}</span>
      </div>
      <div style={{ height: 8, background: '#F0F0F0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: couleur, borderRadius: 4, transition: 'width 0.5s' }} />
      </div>
    </div>
  );
};

// ─── Composant : Timeline ancienneté ──────────────────────────────────────
const TimelineAnciennete = ({ dateEmbauche, annees }) => {
  const jalons = [0, 5, 10, 20];
  const labels = ['Embauche', '5 ans', '10 ans', '20 ans'];
  const pct = Math.min((annees / 25) * 100, 100);

  return (
    <div style={{ margin: '16px 0', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        {labels.map((l, i) => (
          <span key={i} style={{ fontSize: 10, color: annees >= jalons[i] ? VERT_MED : '#bbb', fontWeight: 600 }}>{l}</span>
        ))}
      </div>
      <div style={{ height: 10, background: '#F0F0F0', borderRadius: 5, overflow: 'hidden', position: 'relative' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: `linear-gradient(to right, #A5D6A7, ${VERT})`,
          borderRadius: 5, transition: 'width 0.6s',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        {jalons.map((j, i) => (
          <div key={i} style={{
            width: 8, height: 8, borderRadius: '50%',
            background: annees >= j ? VERT : '#DDD',
            border: `2px solid ${annees >= j ? VERT_MED : '#CCC'}`,
            marginTop: -10, position: 'relative',
          }} />
        ))}
      </div>
      <div style={{ textAlign: 'right', marginTop: 8, fontSize: 11, color: '#666' }}>
        Depuis le {fmtDate(dateEmbauche)} · {annees} ans d'ancienneté
      </div>
    </div>
  );
};

// ─── Modal détail provision ────────────────────────────────────────────────
const ModalDetail = ({ provision, onClose }) => {
  if (!provision) return null;
  const total = parseFloat(provision.total_provision);
  const tranches = [
    { label: 'Tranche 0→5 ans (25%)',   montant: parseFloat(provision.tranche_0_5_ans),   couleur: '#1565C0' },
    { label: 'Tranche 5→10 ans (30%)',  montant: parseFloat(provision.tranche_5_10_ans),  couleur: '#2E7D32' },
    { label: 'Tranche 10→20 ans (45%)', montant: parseFloat(provision.tranche_10_20_ans), couleur: '#E65100' },
    { label: 'Tranche +20 ans (50%)',   montant: parseFloat(provision.tranche_plus_20_ans),couleur: '#6A1B9A' },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
      }} onClick={e => e.stopPropagation()}>

        {/* En-tête */}
        <div style={{ background: `linear-gradient(135deg, ${VERT} 0%, ${VERT_MED} 100%)`, padding: '20px 24px', color: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 16,
                }}>
                  {(provision.prenom?.[0] || '') + (provision.nom?.[0] || '')}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{provision.prenom} {provision.nom}</div>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>{provision.poste || '–'} · {provision.matricule}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                Ancienneté : {provision.anciennete_libelle} · SMM : {formatFCFA(provision.smm)}
              </div>
            </div>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
              borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
            }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Corps */}
        <div style={{ padding: 24 }}>
          {/* Timeline */}
          <TimelineAnciennete
            dateEmbauche={provision.date_embauche}
            annees={provision.anciennete_annees}
          />

          {/* Tranches */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 12 }}>Détail des tranches</div>
            {tranches.map((t, i) => (
              t.montant > 0 &&
              <BarreTranche key={i} label={t.label} montant={t.montant} total={total} couleur={t.couleur} />
            ))}
          </div>

          {/* Total */}
          <div style={{
            background: VERT_L, borderRadius: 10, padding: '16px 20px',
            border: `2px solid ${VERT_MED}`, textAlign: 'center',
          }}>
            <div style={{ fontSize: 11, color: VERT_MED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              TOTAL PROVISION {provision.annee}
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: VERT, marginTop: 4 }}>
              {formatFCFA(provision.total_provision)}
            </div>
            <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>
              Salaire brut annuel : {formatFCFA(provision.salaire_brut_annuel)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════
export default function ProvisionRetraitePage() {
  const anneeActuelle = new Date().getFullYear();
  const [annee, setAnnee]           = useState(anneeActuelle);
  const [provisions, setProvisions] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [calcul, setCalcul]         = useState(false);
  const [filtres, setFiltres]       = useState({ site: '', tranche: '' });
  const [sort, setSort]             = useState({ col: 'total_provision', dir: 'desc' });
  const [detail, setDetail]         = useState(null);
  const [msg, setMsg]               = useState(null);
  const [confirm, setConfirm]       = useState(false);

  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // ─── Chargement des données ──────────────────────────────────────────────
  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ annee });
      if (filtres.site)   params.append('site_id', filtres.site);
      if (filtres.tranche) params.append('tranche', filtres.tranche);
      const r = await axios.get(`${API}/api/provisions?${params}`, { headers });
      setProvisions(r.data.provisions || []);
    } catch {
      setProvisions([]);
    }
    setLoading(false);
  }, [annee, filtres]);

  useEffect(() => { charger(); }, [charger]);

  // ─── Calcul toutes provisions ────────────────────────────────────────────
  const lancerCalcul = async () => {
    setConfirm(false);
    setCalcul(true);
    try {
      const r = await axios.post(`${API}/api/provisions/calculer`, { annee }, { headers });
      setMsg({ type: 'ok', text: `Calcul terminé — ${r.data.nb_calcules} employés traités. Total : ${formatFCFA(r.data.total_general)}` });
      charger();
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Erreur lors du calcul' });
    }
    setCalcul(false);
  };

  // ─── Export Excel ────────────────────────────────────────────────────────
  const exporter = async () => {
    try {
      const r = await axios.get(`${API}/api/provisions/export?annee=${annee}`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `provision_retraite_${annee}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMsg({ type: 'err', text: 'Erreur lors de l\'export Excel' });
    }
  };

  // ─── Tri ─────────────────────────────────────────────────────────────────
  const toggleSort = (col) => {
    setSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' });
  };

  const sorted = [...provisions].sort((a, b) => {
    const va = parseFloat(a[sort.col]) || a[sort.col] || '';
    const vb = parseFloat(b[sort.col]) || b[sort.col] || '';
    if (va < vb) return sort.dir === 'asc' ? -1 : 1;
    if (va > vb) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  // ─── Stats globales ──────────────────────────────────────────────────────
  const totalGeneral = provisions.reduce((s, p) => s + parseFloat(p.total_provision || 0), 0);
  const moyenne = provisions.length > 0 ? totalGeneral / provisions.length : 0;
  const nbPlus20 = provisions.filter(p => p.anciennete_annees >= 20).length;

  // ─── Sites distincts ─────────────────────────────────────────────────────
  // Module 0 : filtre par identifiant de site, libellé = code lisible (jamais l'UUID).
  const sites = [...new Map(provisions.filter(p => p.site_id).map(p => [p.site_id, p.site_code || '–'])).entries()]
    .map(([id, code]) => ({ id, code })).sort((a, b) => a.code.localeCompare(b.code));

  // ─── Icône tri ───────────────────────────────────────────────────────────
  const IcoSort = ({ col }) => {
    if (sort.col !== col) return <span style={{ color: '#ccc', marginLeft: 4 }}>↕</span>;
    return sort.dir === 'asc'
      ? <ChevronUp size={13} style={{ marginLeft: 4 }} />
      : <ChevronDown size={13} style={{ marginLeft: 4 }} />;
  };

  const thStyle = (col) => ({
    padding: '10px 12px', background: VERT, color: '#fff',
    fontWeight: 700, fontSize: 11, textAlign: col === 'nom' ? 'left' : 'right',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    borderRight: '1px solid rgba(255,255,255,0.15)',
  });

  const tdStyle = (align = 'right') => ({
    padding: '9px 12px', fontSize: 12, color: '#333',
    textAlign: align, borderBottom: `1px solid ${GRIS_BORD}`,
    whiteSpace: 'nowrap',
  });

  return (
    <Layout>
      <div style={{ background: GRIS, minHeight: '100vh', padding: '0 0 40px' }}>

        {/* ── Header vert ────────────────────────────────────────────────── */}
        <div className="sg-bandeau-clair" style={{
          background: `linear-gradient(135deg, ${VERT} 0%, ${VERT_MED} 100%)`,
          padding: '20px 28px', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Award size={24} />
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Provision pour Retraite</h1>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>Code du Travail sénégalais · Calcul automatique</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <NotificationBell couleurIcone="#fff" taille={17} />
            <select
              value={annee}
              onChange={e => setAnnee(parseInt(e.target.value))}
              style={{
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {[anneeActuelle - 2, anneeActuelle - 1, anneeActuelle, anneeActuelle + 1].map(a => (
                <option key={a} value={a} style={{ color: '#333', background: '#fff' }}>{a}</option>
              ))}
            </select>
            <div style={{
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600,
            }}>
              Date de référence : 31/12/{annee}
            </div>
          </div>
        </div>

        <div style={{ padding: '24px 28px' }}>

          {/* ── Message flash ──────────────────────────────────────────────── */}
          {msg && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderRadius: 8, marginBottom: 16,
              background: msg.type === 'ok' ? '#E8F5E9' : '#FFEBEE',
              border: `1px solid ${msg.type === 'ok' ? '#A5D6A7' : '#EF9A9A'}`,
              color: msg.type === 'ok' ? '#2E7D32' : ROUGE, fontSize: 13, fontWeight: 600,
            }}>
              {msg.text}
              <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <X size={15} color={msg.type === 'ok' ? '#2E7D32' : ROUGE} />
              </button>
            </div>
          )}

          {/* ── Actions ────────────────────────────────────────────────────── */}
          <div style={{
            background: '#fff', borderRadius: 10, padding: '16px 20px',
            border: `1px solid ${GRIS_BORD}`, marginBottom: 20,
            display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <button
              onClick={() => setConfirm(true)}
              disabled={calcul}
              style={{
                background: VERT, color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                opacity: calcul ? 0.7 : 1,
              }}
            >
              {calcul
                ? <><RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />Calcul en cours...</>
                : <><Calculator size={15} />Calculer les provisions {annee}</>
              }
            </button>
            <button
              onClick={exporter}
              disabled={provisions.length === 0}
              style={{
                background: '#fff', color: VERT, border: `1.5px solid ${VERT}`,
                borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
                opacity: provisions.length === 0 ? 0.4 : 1,
              }}
            >
              <Download size={15} />Exporter Excel
            </button>
            <div style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
              {provisions.length} employé{provisions.length > 1 ? 's' : ''} · Année {annee}
            </div>
          </div>

          {/* ── Confirmation calcul ─────────────────────────────────────────── */}
          {confirm && (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 900,
            }}>
              <div style={{
                background: '#fff', borderRadius: 12, padding: 28, maxWidth: 380, width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)', textAlign: 'center',
              }}>
                <AlertTriangle size={40} color="#FF6F00" style={{ marginBottom: 12 }} />
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                  Recalculer les provisions {annee} ?
                </div>
                <div style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
                  Ceci recalculera et écrasera les provisions existantes pour l'année {annee} sur la base des données actuelles.
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button onClick={() => setConfirm(false)} style={{
                    padding: '10px 20px', borderRadius: 8, border: `1px solid ${GRIS_BORD}`,
                    background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                  }}>Annuler</button>
                  <button onClick={lancerCalcul} style={{
                    padding: '10px 24px', borderRadius: 8, border: 'none',
                    background: VERT, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                  }}>Confirmer</button>
                </div>
              </div>
            </div>
          )}

          {/* ── Stats globales ─────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <StatCard label="Employés concernés" valeur={provisions.length} sous="avec provision calculée" Ico={Users} color={VERT} />
            <StatCard label="Total provision" valeur={totalGeneral > 0 ? formatFCFA(totalGeneral) : '–'} sous={`Exercice ${annee}`} Ico={TrendingUp} color="#1565C0" />
            <StatCard label="Provision moyenne" valeur={moyenne > 0 ? formatFCFA(moyenne) : '–'} sous="par employé" Ico={Calculator} color="#E65100" />
            <StatCard label="+20 ans d'ancienneté" valeur={nbPlus20} sous="tranche maximale (50%)" Ico={Award} color="#6A1B9A" />
          </div>

          {/* ── Filtres ────────────────────────────────────────────────────── */}
          <div style={{
            background: '#fff', borderRadius: 10, padding: '12px 16px',
            border: `1px solid ${GRIS_BORD}`, marginBottom: 16,
            display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <Filter size={14} color="#888" />
            <select
              value={filtres.site}
              onChange={e => setFiltres(f => ({ ...f, site: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, fontSize: 12, color: '#555' }}
            >
              <option value="">Tous les sites</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.code}</option>)}
            </select>
            <select
              value={filtres.tranche}
              onChange={e => setFiltres(f => ({ ...f, tranche: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, fontSize: 12, color: '#555' }}
            >
              <option value="">Toutes les tranches</option>
              <option value="0-5">Moins de 5 ans</option>
              <option value="5-10">5 à 10 ans</option>
              <option value="10-20">10 à 20 ans</option>
              <option value="20+">Plus de 20 ans</option>
            </select>
            {(filtres.site || filtres.tranche) && (
              <button onClick={() => setFiltres({ site: '', tranche: '' })} style={{
                background: 'none', border: 'none', color: '#999', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
              }}>
                <X size={12} />Réinitialiser
              </button>
            )}
          </div>

          {/* ── Tableau ────────────────────────────────────────────────────── */}
          <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${GRIS_BORD}`, overflow: 'hidden' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>
                <RefreshCw size={32} color="#ccc" />
                <div style={{ marginTop: 12, fontSize: 13 }}>Chargement...</div>
              </div>
            ) : provisions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>
                <TrendingUp size={48} color="#ddd" />
                <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600 }}>Aucune provision calculée</div>
                <div style={{ fontSize: 12, color: '#bbb', marginTop: 4 }}>
                  Cliquez sur "Calculer les provisions {annee}" pour démarrer
                </div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                  <thead>
                    <tr>
                      {[
                        { label: 'Matricule', col: 'matricule', align: 'left' },
                        { label: 'Nom & Prénom', col: 'nom', align: 'left' },
                        { label: 'Fonction', col: 'poste', align: 'left' },
                        { label: 'Site', col: 'site_code', align: 'left' },
                        { label: 'Ancienneté', col: 'anciennete_annees', align: 'right' },
                        { label: 'SMM', col: 'smm', align: 'right' },
                        { label: '0→5 ans', col: 'tranche_0_5_ans', align: 'right' },
                        { label: '5→10 ans', col: 'tranche_5_10_ans', align: 'right' },
                        { label: '10→20 ans', col: 'tranche_10_20_ans', align: 'right' },
                        { label: '+20 ans', col: 'tranche_plus_20_ans', align: 'right' },
                        { label: 'TOTAL', col: 'total_provision', align: 'right' },
                        { label: '', col: null },
                      ].map((h, i) => (
                        <th
                          key={i}
                          onClick={() => h.col && toggleSort(h.col)}
                          style={{
                            ...thStyle(h.col),
                            textAlign: h.align || 'right',
                            cursor: h.col ? 'pointer' : 'default',
                          }}
                        >
                          {h.label}{h.col && <IcoSort col={h.col} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((p, idx) => (
                      <tr
                        key={p.id}
                        style={{ background: couleurLigne(p.anciennete_annees), cursor: 'pointer' }}
                        onClick={() => setDetail(p)}
                      >
                        <td style={{ ...tdStyle('left'), fontFamily: 'monospace', fontSize: 11, color: '#888' }}>{p.matricule}</td>
                        <td style={{ ...tdStyle('left'), fontWeight: 600 }}>{p.prenom} {p.nom}</td>
                        <td style={{ ...tdStyle('left'), color: '#555', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.poste || '–'}</td>
                        <td style={{ ...tdStyle('left') }}>
                          <span style={{ background: '#E3F2FD', color: '#1565C0', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>
                            {p.site_code || '–'}
                          </span>
                        </td>
                        <td style={{ ...tdStyle('right'), fontWeight: 600 }}>{p.anciennete_libelle}</td>
                        <td style={{ ...tdStyle('right') }}>{formatFCFA(p.smm)}</td>
                        <td style={{ ...tdStyle('right'), color: parseFloat(p.tranche_0_5_ans) > 0 ? '#1565C0' : '#ccc' }}>
                          {parseFloat(p.tranche_0_5_ans) > 0 ? formatFCFA(p.tranche_0_5_ans) : '–'}
                        </td>
                        <td style={{ ...tdStyle('right'), color: parseFloat(p.tranche_5_10_ans) > 0 ? '#2E7D32' : '#ccc' }}>
                          {parseFloat(p.tranche_5_10_ans) > 0 ? formatFCFA(p.tranche_5_10_ans) : '–'}
                        </td>
                        <td style={{ ...tdStyle('right'), color: parseFloat(p.tranche_10_20_ans) > 0 ? '#E65100' : '#ccc' }}>
                          {parseFloat(p.tranche_10_20_ans) > 0 ? formatFCFA(p.tranche_10_20_ans) : '–'}
                        </td>
                        <td style={{ ...tdStyle('right'), color: parseFloat(p.tranche_plus_20_ans) > 0 ? '#6A1B9A' : '#ccc' }}>
                          {parseFloat(p.tranche_plus_20_ans) > 0 ? formatFCFA(p.tranche_plus_20_ans) : '–'}
                        </td>
                        <td style={{ ...tdStyle('right'), fontWeight: 800, color: VERT, fontSize: 13 }}>
                          {formatFCFA(p.total_provision)}
                        </td>
                        <td style={{ ...tdStyle('center'), padding: '9px 8px' }}>
                          <button
                            onClick={e => { e.stopPropagation(); setDetail(p); }}
                            style={{
                              background: VERT_L, border: 'none', borderRadius: 6,
                              padding: '4px 8px', cursor: 'pointer', color: VERT,
                              display: 'inline-flex', alignItems: 'center',
                            }}
                          >
                            <Eye size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* ── Total général ── */}
                  <tfoot>
                    <tr style={{ background: VERT }}>
                      <td colSpan={10} style={{ padding: '12px 16px', color: '#fff', fontWeight: 800, fontSize: 13, textAlign: 'right' }}>
                        TOTAL GÉNÉRAL ({provisions.length} employés)
                      </td>
                      <td style={{ padding: '12px 12px', color: '#fff', fontWeight: 900, fontSize: 15, textAlign: 'right' }}>
                        {formatFCFA(totalGeneral)}
                      </td>
                      <td style={{ padding: '12px 8px' }} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* ── Copyright ────────────────────────────────────────────────────── */}
          <div style={{ textAlign: 'center', marginTop: 32, fontSize: 11, color: '#bbb' }}>
            © {annee} SOGAS · Abdoul Aziz Talla
          </div>
        </div>
      </div>

      {/* ── Modal détail ────────────────────────────────────────────────────── */}
      {detail && <ModalDetail provision={detail} onClose={() => setDetail(null)} />}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Layout>
  );
}
